import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { collection, addDoc, getDocs, query, where, doc, getDoc, setDoc, increment, arrayUnion, serverTimestamp, limit, runTransaction, updateDoc, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAuth } from '../context/AuthContext';
import './SubscriptionReview.css';

const SubscriptionReview = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const [user, userLoading] = useAuthState(auth);
    const { currentUser } = useAuth();

    // DYNAMIC DURATIONS & DISCOUNTS
    const [discountDurations, setDiscountDurations] = useState([]);
    const [dayDiscountPercent, setDayDiscountPercent] = useState(0);
    const [duration, setDuration] = useState(0);

    // COUPONS
    const [studentCoupon, setStudentCoupon] = useState(null);

    // UTILITY STATES
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDates, setSelectedDates] = useState([]);
    const [couponCode, setCouponCode] = useState('');
    const [couponMessage, setCouponMessage] = useState({ type: '', text: '' });
    const [isCouponLoading, setIsCouponLoading] = useState(false);
    const [discountInfo, setDiscountInfo] = useState({
        applied: false,
        percent: 0,
        value: 0,
        code: null,
        type: null,
        couponDocId: null,
        referrerDocId: null,
        referrerCustomId: null
    });
    
    // MODAL STATES
    const [showTnCModal, setShowTnCModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    useEffect(() => {
        if (!state) {
            navigate(`/category/${categoryId}/meals`);
            return;
        }
        
        const fetchData = async () => {
            try {
                // Fetch duration discounts
                const discountsQuery = query(collection(db, 'days_discounts'), orderBy('subscriptionDuration'));
                const durationsSnapshot = await getDocs(discountsQuery);
                const durationsData = durationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                setDiscountDurations(durationsData);
                if (durationsData.length > 0) {
                    setDuration(durationsData[0].subscriptionDuration);
                    setDayDiscountPercent(durationsData[0].percentage || 0);
                }

                // Fetch the special student coupon (STUDENT500)
                const couponQuery = query(collection(db, 'coupons'), where('code', '==', 'STUDENT500'), limit(1));
                const couponSnapshot = await getDocs(couponQuery);
                if (!couponSnapshot.empty) {
                    setStudentCoupon({ id: couponSnapshot.docs[0].id, ...couponSnapshot.docs[0].data() });
                }

            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };
        fetchData();
    }, [state, navigate, categoryId]);

    // Automatically apply/remove the student discount based on user type and duration
    useEffect(() => {
        const isVerifiedStudent = currentUser?.locationType === 'student' && 
                                  (currentUser?.studentIdVerified === 'approved' || currentUser?.studentIdVerified === 'verified');

        if (isVerifiedStudent && duration === 30 && studentCoupon) {
            setDiscountInfo({
                applied: true,
                percent: 0,
                value: studentCoupon.discountValue,
                code: studentCoupon.code,
                type: 'student_coupon',
                couponDocId: studentCoupon.id,
            });
            setCouponMessage({ type: 'success', text: `Student Offer applied! You get ₹${studentCoupon.discountValue} off.` });
        } 
        else if (discountInfo.type === 'student_coupon' && (duration !== 30 || !isVerifiedStudent)) {
            setDiscountInfo({ applied: false, percent: 0, value: 0, code: null, type: null, couponDocId: null });
            setCouponMessage({ type: '', text: '' });
        }
    }, [currentUser, duration, studentCoupon]);

    const { packageData, selectedMeals, subtotal } = state || { selectedMeals: {}, packageData: {}, subtotal: 0 };
    
    const handleDurationChange = (newDuration, newDiscount) => {
        setDuration(newDuration);
        setDayDiscountPercent(newDiscount || 0);
        setSelectedDates([]);
    };

    const handleApplyCoupon = async () => {
        const codeToApply = couponCode.trim().toUpperCase();
        if (!codeToApply) {
            setCouponMessage({ type: 'error', text: 'Please enter a code.' });
            return;
        }
        if (!user) {
            alert("Please login before applying a code.");
            return;
        }
        setIsCouponLoading(true);
        setCouponMessage({ type: '', text: '' });
    
        try {
            // For student-only coupons, always fetch the latest user data to prevent stale context issues.
            const userDocRef = doc(db, 'customer', user.uid);
            const userDocSnap = await getDoc(userDocRef);
    
            if (!userDocSnap.exists()) {
                setCouponMessage({ type: 'error', text: 'Could not verify your user profile.' });
                setIsCouponLoading(false);
                return;
            }
            
            const latestUserData = userDocSnap.data();
            const isVerifiedStudent = latestUserData.locationType === 'student' && 
                                      (latestUserData.studentIdVerified === 'approved' || latestUserData.studentIdVerified === 'verified');
    
            // Calculate original amount before discount
            const originalAmount = (subtotal || 0) * selectedDates.length;
            const priceAfterDayDiscount = originalAmount * (1 - (dayDiscountPercent / 100));
    
            // Special handling for the STUDENT500 coupon
            if (codeToApply === 'STUDENT500') {
                if (isVerifiedStudent && duration === 30) {
                    if (studentCoupon) {
                        const discountAmount = studentCoupon.discountValue;
                        
                        setDiscountInfo({
                            applied: true,
                            percent: 0,
                            value: studentCoupon.discountValue,
                            code: studentCoupon.code,
                            type: 'student_coupon',
                            couponDocId: studentCoupon.id,
                        });
                        setCouponMessage({ type: 'success', text: `Student Offer applied! You get ₹${studentCoupon.discountValue} off.` });
                    }
                } else if (duration !== 30) {
                    setCouponMessage({ type: 'error', text: 'The Student Offer is only valid for 30-day subscriptions.' });
                } else {
                    setCouponMessage({ type: 'error', text: 'This coupon is for verified students only.' });
                }
                setIsCouponLoading(false);
                return;
            }
    
            // Generic coupon logic
            const couponQuery = query(collection(db, "coupons"), where("code", "==", codeToApply), limit(1));
            const couponSnapshot = await getDocs(couponQuery);
    
            if (couponSnapshot.empty) {
                setCouponMessage({ type: 'error', text: 'The entered coupon code is not valid.' });
            } else {
                const couponDoc = couponSnapshot.docs[0];
                const couponData = couponDoc.data();
                const today = new Date();
                today.setHours(0, 0, 0, 0);
    
                if (couponData.isStudentOnly && !isVerifiedStudent) {
                    setCouponMessage({ type: 'error', text: 'This coupon is for verified students only.' });
                } else if (!couponData.isActive) {
                    setCouponMessage({ type: 'error', text: 'This coupon is no longer active.' });
                } else if (couponData.validFrom && new Date(couponData.validFrom) > today) {
                    setCouponMessage({ type: 'error', text: 'This coupon is not yet valid.' });
                } else if (couponData.validUntil && new Date(couponData.validUntil) < today) {
                    setCouponMessage({ type: 'error', text: 'This coupon has expired.' });
                } else {
                    // Calculate discount amount
                    let discountedAmount = 0;
                    if (couponData.discountPercentage > 0) {
                        discountedAmount = priceAfterDayDiscount * (couponData.discountPercentage / 100);
                    } else if (couponData.discountValue > 0) {
                        discountedAmount = couponData.discountValue;
                    }
                    
                    setDiscountInfo({
                        applied: true,
                        percent: couponData.discountPercentage || 0,
                        value: couponData.discountValue || 0,
                        code: couponData.code,
                        type: 'coupon',
                        couponDocId: couponDoc.id,
                    });
                    setCouponMessage({ type: 'success', text: `Coupon "${couponData.code}" applied successfully!` });
                }
            }
        } catch (err) {
            console.error("Error applying code:", err);
            setCouponMessage({ type: 'error', text: 'Could not apply code. Please try again.' });
        } finally {
            setIsCouponLoading(false);
        }
    };
    
    const { subtotalAfterDiscounts, gstAmount, finalTotalPrice, couponDiscountValue, basePrice, priceAfterDayDiscount } = useMemo(() => {
        const basePrice = (subtotal || 0) * selectedDates.length;
        if (basePrice === 0) {
            return { 
                subtotalAfterDiscounts: 0, 
                gstAmount: 0, 
                finalTotalPrice: 0, 
                couponDiscountValue: 0,
                basePrice: 0,
                priceAfterDayDiscount: 0
            };
        }
        
        const priceAfterDayDiscount = basePrice * (1 - (dayDiscountPercent / 100));
        
        let calculatedCouponDiscount = 0;
        if (discountInfo.applied) {
            if (discountInfo.percent > 0) {
                calculatedCouponDiscount = priceAfterDayDiscount * (discountInfo.percent / 100);
            } else if (discountInfo.value > 0) {
                calculatedCouponDiscount = discountInfo.value;
            }
        }

        const finalSubtotal = priceAfterDayDiscount - calculatedCouponDiscount;
        const calculatedGst = finalSubtotal * 0.05; // 5% GST
        const finalTotal = finalSubtotal + calculatedGst;
        
        return {
            subtotalAfterDiscounts: finalSubtotal,
            gstAmount: calculatedGst,
            finalTotalPrice: Math.round(finalTotal < 0 ? 0 : finalTotal),
            couponDiscountValue: calculatedCouponDiscount,
            basePrice: basePrice,
            priceAfterDayDiscount: priceAfterDayDiscount
        };
    }, [subtotal, selectedDates, dayDiscountPercent, discountInfo]);
    
    const handleConfirm = async () => {
        if (!user) { alert('Please login to finalize your subscription!'); return; }
        
        setIsSubscribing(true);
        try {
            const selectedComboForDb = {};
            Object.entries(selectedMeals).forEach(([type, pkg]) => {
                if (pkg) {
                    selectedComboForDb[type] = { packageId: pkg.id, name: pkg.name, price: pkg.price };
                }
            });
            
            if ((discountInfo.type === 'coupon' || discountInfo.type === 'student_coupon') && discountInfo.couponDocId) {
                const couponRef = doc(db, 'coupons', discountInfo.couponDocId);
                await updateDoc(couponRef, { usedCount: increment(1) });
                
                // Save coupon application to Firebase - MOVED HERE FROM handleApplyCoupon
                await addDoc(collection(db, 'coupon_applied'), {
                    couponCode: discountInfo.code,
                    originalAmount: priceAfterDayDiscount,
                    userName: user.displayName || user.email.split('@')[0],
                    userEmail: user.email,
                    userId: user.uid,
                    discountedAmount: couponDiscountValue,
                    appliedAt: serverTimestamp(),
                    couponType: discountInfo.type === 'student_coupon' ? 'student_coupon' : 'regular_coupon'
                });
            }

            let cancellationPolicy = { allowed: 0, fullRefundCount: 0 };
            const totalDays = selectedDates.length;
    
            if (totalDays >= 30) { cancellationPolicy = { allowed: 8, fullRefundCount: 4 }; } 
            else if (totalDays >= 7) { cancellationPolicy = { allowed: 1, fullRefundCount: 1 }; }

            const subscriptionPayload = {
                customerId: user.uid, 
                customerName: user.displayName || user.email.split('@')[0], 
                customerEmail: user.email,
                packageName: packageData?.name, 
                cuisineType: packageData?.cuisineType, 
                selectedCombo: selectedComboForDb,
                subscriptionDuration: totalDays, 
                selectedDates: selectedDates.map(d => d.toISOString()),
                dailySubtotal: subtotal, 
                totalPrice: finalTotalPrice, 
                appliedCoupon: discountInfo.applied ? discountInfo.code : null,
                requestDate: serverTimestamp(), 
                requestStatus: 'pending', 
                cancellationPolicy: cancellationPolicy,
                cancellationsUsed: 0, 
                cancelledDates: [],
                basePrice: basePrice,
                dayDiscountPercent: dayDiscountPercent,
                priceAfterDayDiscount: priceAfterDayDiscount,
                couponDiscount: couponDiscountValue
            };
            
            await addDoc(collection(db, 'package_request'), subscriptionPayload);

            setShowTnCModal(false);
            setShowSuccessModal(true);

        } catch (err) {
            console.error("Error confirming subscription:", err);
            alert("There was an error submitting your request.");
        } finally {
            setIsSubscribing(false);
        }
    };
    
    const handleCheckoutClick = () => {
        if (selectedDates.length === 0) {
            alert('Please select a start date from the calendar.');
            return;
        }
        setShowTnCModal(true);
    };

    const handleCloseModal = () => {
        setShowSuccessModal(false);
        navigate('/package');
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const days = Array(firstDayOfMonth).fill(null);
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        while (days.length % 7 !== 0) {
            days.push(null);
        }
        return days;
    };

    const isDateSelected = (date) => selectedDates.some(d => d.toDateString() === date.toDateString());

    const isDateDisabled = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const handleDateClick = (date) => {
        if (isDateDisabled(date)) return;
        const newSelectedDates = [];
        let currentDate = new Date(date);
        for (let i = 0; i < duration; i++) {
            newSelectedDates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        setSelectedDates(newSelectedDates);
    };

    const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));

    if (!state || userLoading) {
        return <div className="status-container">Loading...</div>;
    }

    return (
        <div className="review-page-container">
            <div className="date-selection-area">
                <div className="options-card mobile-first">
                    <div className="duration-buttons">
                        {discountDurations.map(d => (
                            <button key={d.id} onClick={() => handleDurationChange(d.subscriptionDuration, d.percentage)} className={`duration-btn ${duration === d.subscriptionDuration ? 'active' : ''}`}>
                                {d.subscriptionDuration} DAYS
                                {d.percentage > 0 && <span className="new-tag">{d.percentage}% OFF</span>}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="calendar-container">
                     <div className="calendar-header">
                        <button onClick={prevMonth} className="calendar-nav">‹</button>
                        <h4>{currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' })}</h4>
                        <button onClick={nextMonth} className="calendar-nav">›</button>
                    </div>

                    <div className="calendar-grid">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="weekday">{day.substring(0, 3)}</div>
                        ))}
                        {getDaysInMonth(currentMonth).map((date, index) => (
                            <div key={index} className="calendar-day-container">
                                {date ? (
                                    <button
                                        className={`day-button ${isDateDisabled(date) ? 'disabled' : ''} ${isDateSelected(date) ? 'selected' : ''}`}
                                        onClick={() => handleDateClick(date)}
                                        disabled={isDateDisabled(date)}
                                    >
                                        {date.getDate()}
                                    </button>
                                ) : null}
                            </div>
                        ))}
                    </div>

                    <p className="calendar-note">
                        Your subscription will be for {selectedDates.length > 0 ? selectedDates.length : duration} days, starting from your selected date.
                    </p>
                </div>
            </div>

            <div className="summary-area">
                <div className="options-card desktop-only">
                    <div className="duration-buttons">
                        {discountDurations.map(d => (
                            <button key={d.id} onClick={() => handleDurationChange(d.subscriptionDuration, d.percentage)} className={`duration-btn ${duration === d.subscriptionDuration ? 'active' : ''}`}>
                                {d.subscriptionDuration} DAYS
                                {d.percentage > 0 && <span className="new-tag">{d.percentage}% OFF</span>}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="summary-card">
                    <h3 className="summary-title">Your Subscription Details</h3>
                    <div className="summary-item">
                        <span>Cuisine</span>
                        <span>{packageData?.cuisineType}</span>
                    </div>
                    
                    <h4 className="summary-combo-title">Meal Combo</h4>
                    {Object.entries(selectedMeals).map(([type, pkg]) => pkg && (
                        <div key={type} className="summary-meal-item">
                            <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                            <span>{pkg.name}</span>
                            <span>₹{pkg.price}</span>
                        </div>
                    ))}
                    
                    <div className="summary-subtotal">
                        <span>Base Price ({selectedDates.length} days)</span>
                        <span>₹{Math.round(basePrice)}</span>
                    </div>
                    
                    {dayDiscountPercent > 0 && selectedDates.length > 0 && (
                         <div className="summary-item discount">
                            <span>Duration Discount ({dayDiscountPercent}%)</span>
                            <span>- ₹{Math.round(basePrice * (dayDiscountPercent / 100))}</span>
                        </div>
                    )}

                    <div className="coupon-section">
                        <input type="text" placeholder="Coupon Code (Optional)" className="coupon-input"
                            value={couponCode} onChange={(e) => setCouponCode(e.target.value)} disabled={isCouponLoading || discountInfo.applied} />
                        <button className="apply-coupon-btn" onClick={handleApplyCoupon}
                            disabled={isCouponLoading || discountInfo.applied}>
                            {isCouponLoading ? '...' : (discountInfo.applied ? '✓ Applied' : 'Apply')}
                        </button>
                    </div>

                    {couponMessage.text && (
                        <div className={`promo-banner ${couponMessage.type}`}>
                            <span className="promo-icon">{couponMessage.type === 'success' ? '✅' : '⚠️'}</span>
                            {couponMessage.text}
                        </div>
                    )}
                     
                     {discountInfo.applied && selectedDates.length > 0 && (
                         <div className="summary-item discount">
                            <span>{discountInfo.type === 'student_coupon' ? 'Student Offer' : 'Coupon Discount'}</span>
                            <span>- ₹{Math.round(couponDiscountValue)}</span>
                        </div>
                    )}
                    
                    <div className="summary-subtotal">
                        <span>Subtotal after discounts</span>
                        <span>₹{Math.round(subtotalAfterDiscounts)}</span>
                    </div>

                    <div className="summary-item gst">
                        <span>GST (5%)</span>
                        <span>+ ₹{Math.round(gstAmount)}</span>
                    </div>
                    
                     <div className="summary-subtotal total">
                        <span>Total Payable</span>
                        <span>₹{finalTotalPrice}</span>
                    </div>
                    
                    <button className="confirm-btn" onClick={handleCheckoutClick} disabled={isSubscribing || selectedDates.length === 0}>
                        Checkout
                    </button>
                </div>
            </div>

            {showTnCModal && (
                <div className="modal-overlay-custom">
                    <div className="modal-content-custom tnc-modal">
                        <h3>Terms and Conditions</h3>
                        <div className="tnc-text-area">
                            <p><strong>1. Introduction</strong></p>
                            <p>Welcome to TOVEN! These Terms and Conditions ("Terms") govern your use of our food delivery services. By using our services, you agree to these Terms.</p>
                            
                            <p><strong>2. Service Description</strong></p>
                            <p>TOVEN operates a pre-order only food delivery service providing homemade-quality meals, fitness diets, add-ons, and party orders. All meals are prepared fresh based on advance orders.</p>
                            
                            <p><strong>3. Ordering and Payment</strong></p>
                            <p>• All orders must be placed in advance through our platform</p>
                            <p>• Payment is required at the time of order confirmation</p>
                            <p>• Prices are subject to change without prior notice</p>
                            <p>• We accept various payment methods as displayed on our platform</p>
                            
                            <p><strong>4. Delivery Policy</strong></p>
                            <p>• Delivery times are estimates and may vary due to unforeseen circumstances</p>
                            <p>• You must provide accurate delivery address and contact information</p>
                            <p>• We are not liable for missed deliveries due to incorrect information provided by you</p>
                            <p>• Delivery charges may apply as specified during checkout</p>
                            
                            <p><strong>5. Cancellation and Refund Policy</strong></p>
                            <p>• Trial packs (3 days): Non-cancellable and non-refundable</p>
                            <p>• 7-day plans: 1 cancellation with full refund permitted</p>
                            <p>• 30-day plans: 4 cancellations with full refund and 4 additional cancellations with 50% refund permitted</p>
                            <p>• Cancellation requests must be made at least 24 hours before scheduled delivery</p>
                            <p>• Refunds will be processed within 7-10 business days</p>
                            
                            <p><strong>6. Food Safety and Quality</strong></p>
                            <p>• We maintain high standards of food safety and hygiene</p>
                            <p>• All meals are prepared in certified kitchens</p>
                            <p>• Please inform us of any food allergies or dietary restrictions</p>
                            <p>• Consume delivered food within recommended time frames</p>
                            
                            <p><strong>7. User Responsibilities</strong></p>
                            <p>• Provide accurate personal and delivery information</p>
                            <p>• Be available to receive deliveries at scheduled times</p>
                            <p>• Report any issues with orders promptly</p>
                            <p>• Use our services in accordance with these Terms</p>
                            
                            <p><strong>8. Limitation of Liability</strong></p>
                            <p>TOVEN's liability is limited to the value of the order. We are not liable for any indirect, incidental, or consequential damages.</p>
                            
                            <p><strong>9. Privacy Policy</strong></p>
                            <p>Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your information.</p>
                            
                            <p><strong>10. Modifications</strong></p>
                            <p>We reserve the right to modify these Terms at any time. Continued use of our services constitutes acceptance of modified Terms.</p>
                            
                            <p><strong>11. Contact Information</strong></p>
                            <p>For any questions or concerns regarding these Terms, please contact us through our customer support channels.</p>
                            
                            <p><strong>12. Governing Law</strong></p>
                            <p>These Terms are governed by the laws of India. Any disputes will be subject to the jurisdiction of Indian courts.</p>
                        </div>
                        <div className="tnc-buttons">
                            <button onClick={() => setShowTnCModal(false)} className="modal-button-custom cancel">Cancel</button>
                            <button onClick={handleConfirm} className="modal-button-custom agree" disabled={isSubscribing}>
                                {isSubscribing ? 'Processing...' : 'I Agree & Subscribe'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSuccessModal && (
                <div className="modal-overlay-custom">
                    <div className="modal-content-custom">
                        <span className="modal-icon-custom">✅</span>
                        <h3>Success!</h3>
                        <p>Subscription request submitted successfully! Please contact the team for activation.</p>
                        <button onClick={handleCloseModal} className="modal-button-custom">OK</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionReview;